import React from 'react'
import SettingsBox from '../../Data/SettingsBox'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { BatteryModel } from 'src/Libs/batteries'
import { Box, Grid, SxProps } from '@mui/material'
import GraphicBaseCard from 'src/Libs/graph-cards/components/GraphicBaseCard'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { makeStyles } from 'tss-react/mui'
import { getTachoGraphValues } from 'src/Libs/system-settings/utilities/default'
import { useGraphicBaseCard } from 'src/Libs/graph-cards'
import { UnitTypes } from 'src/Libs/types'

const useStyles = makeStyles()(() => ({
  textField: {
    '.MuiInputBase-input': {
      textAlign: 'right',
    },
  },
}))

interface Props {
  model: BatteryModel
}
interface SingleValueProps {
  value: number
  decimals?: number
  suffix?: string
  fullWidth?: boolean
  sx?: SxProps
}

const NumericValue: React.VFC<SingleValueProps> = ({ value, decimals = 0, suffix = '', ...rest }) => {
  const { classes } = useStyles()
  return <TextField value={value.toFixed(decimals)} className={classes.textField} suffix={suffix} disabled clearable={false} {...rest} />
}

const BatteryOverviewData: React.VFC<Props> = ({ model }) => {
  const { t } = useCustomTranslation()
  const { minMaxTagLabel, valueOnlyTagLabel } = useGraphicBaseCard()

  const settingsList = [
    {
      title: t('systemSettings.battery.overview.countModules').toString(),
      value: <NumericValue value={model.countModules} data-qa="countModules" fullWidth />,
    },
  ]

  const lineItems = settingsList.map((item, index) => (
    <SettingsBox data-qa={`settings-box-${index}`} key={index} title={item.title} suffix={item.value} variant={'fullWidth'} disabled />
  ))

  const currentValue = getTachoGraphValues(model.current.values.length ?? 0, model.current.min ?? 0, model.current.values)
  const currentVoltage = getTachoGraphValues(model.voltage.values.length ?? 0, model.voltage.min ?? 0, model.voltage.values)
  const currentTemperature = getTachoGraphValues(model.temperature.values.length ?? 0, model.temperature.min ?? 0, model.temperature.values)

  return (
    <Box sx={{ p: 3 }}>
      <Grid container direction="row" justifyContent="space-between" spacing={3} sx={{ pb: 3 }}>
        <Grid item xs={12} sm={4}>
          <GraphicBaseCard
            tagLabel={valueOnlyTagLabel}
            min={model.current.min}
            max={model.current.max}
            values={currentValue}
            title={t('systemSettings.battery.overview.current')}
            unit={UnitTypes.ampere}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <GraphicBaseCard
            tagLabel={valueOnlyTagLabel}
            min={model.voltage.min}
            max={model.voltage.max}
            values={currentVoltage}
            title={t('systemSettings.battery.overview.voltage')}
            unit={UnitTypes.volt}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <GraphicBaseCard
            tagLabel={minMaxTagLabel}
            values={currentTemperature}
            min={model.temperature.min}
            max={model.temperature.max}
            title={t('systemSettings.battery.overview.temperature')}
            unit={UnitTypes.celsius}
          />
        </Grid>
      </Grid>
      <Grid container>
        {lineItems.map((lineItem, index) => (
          <Grid item key={index} xs={12} md={12} lg={12}>
            {lineItem}
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default BatteryOverviewData
